/**
 * Sprint S13.3.A — pricing page (public).
 *
 * Founding/standard tiers, founding-remaining counter, currency picker
 * (display only — Stripe Checkout auto-detects the customer's actual currency
 * at checkout time). "Subscribe" → POST /billing/checkout-session → redirect
 * to Stripe.
 *
 * Day 12: annual billing scope dropped (was misleading — "Pay annually" UX
 * promised a single £290 upfront + 12-month commit but the implementation
 * was monthly billing + 16.67% coupon). Future restoration would need true
 * annual Stripe Prices (£290/year single charge).
 *
 * Conventions: default export; `api` from @/api/client (baseURL already
 * /api, paths omit the prefix); i18n billing.* keys; project Tailwind
 * tokens (cream/navy/amber/taupe — named shades, no numeric scale).
 * Functional strawman pending runtime design review.
 */
import { LoadError } from "@/components/ui/LoadError";
import { NotificationPreferencesLink } from "@/components/settings/NotificationPreferences";
import { AccountDataLink } from "@/components/AccountDataLink";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth";
import { billingCopy, billingError } from "@/lib/billingCopy";
import { Link } from "react-router-dom";
import { api, PAYWALL_REASON_KEY, type SessionBoundRequestConfig } from "@/api/client";

interface FoundingCounter {
  founding_active: number;
  founding_cap: number;
  founding_remaining: number;
  next_tier: "founding" | "standard";
}

const PRICES = {
  founding: { GBP: "£29", EUR: "€35", USD: "$39" },
  standard: { GBP: "£39", EUR: "€44", USD: "$49" },
} as const;

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const token = useAuthStore(state => state.token);
  const busy = useRef(false);
  // How the athlete got here: fresh from signup ("welcome"), bounced by the
  // 402 interceptor (the reason it stashed), or straight off the marketing
  // site (null — no banner, just the prices).
  const [arrival] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    if (new URLSearchParams(window.location.search).get("welcome") === "1") {
      sessionStorage.removeItem(PAYWALL_REASON_KEY);
      return "welcome";
    }
    const reason = sessionStorage.getItem(PAYWALL_REASON_KEY);
    if (reason) sessionStorage.removeItem(PAYWALL_REASON_KEY);
    return reason;
  });
  const [counter, setCounter] = useState<FoundingCounter | null>(null);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState<"GBP" | "EUR" | "USD">("GBP");
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState("");
  useEffect(() => { busy.current = false; setLoading(false); setError(null); setErrorCode(""); }, [token]);

  const [pending, setPending] = useState<{ tier: string; currency: "GBP" | "EUR" | "USD" } | null>(null);
  const [accountLoading, setAccountLoading] = useState(!!token);
  const [accountError, setAccountError] = useState(false);
  const [counterError, setCounterError] = useState(false);
  const [counterAttempt, setCounterAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCounterError(false);
    api
      .get<FoundingCounter>("/billing/founding-counter", { signal: controller.signal })
      .then((res) => setCounter(res.data))
      .catch(() => { if (!controller.signal.aborted) setCounterError(true); });
    return () => controller.abort();
  }, [counterAttempt]);

  useEffect(() => {
    const controller = new AbortController();
    setPending(null); setAccountError(false); setAccountLoading(!!token);
    if (token) api.get<{ pending_checkout?: { tier: string; currency: "GBP" | "EUR" | "USD" } | null }>("/billing/my-subscription", { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted || useAuthStore.getState().token !== token) return;
        const value = data.pending_checkout ?? null;
        setPending(value);
        if (value && ["GBP", "EUR", "USD"].includes(value.currency)) setCurrency(value.currency);
      }).catch(() => { if (!controller.signal.aborted) setAccountError(true); })
      .finally(() => { if (!controller.signal.aborted && useAuthStore.getState().token === token) setAccountLoading(false); });
    return () => controller.abort();
  }, [token, counterAttempt]);

  async function handleSubscribe(tier: "founding" | "standard") {
    if (busy.current) return;
    busy.current = true;
    const ownerToken = token;
    setLoading(true);
    setError(null);
    setErrorCode("");
    try {
      const res = await api.post<{ url: string; session_id: string }>(
        "/billing/checkout-session", { tier, currency }, { expectedSession: ownerToken } as SessionBoundRequestConfig,
      );
      if (useAuthStore.getState().token !== ownerToken) return;
      window.location.href = res.data.url;
    } catch (err: unknown) {
      if (useAuthStore.getState().token !== ownerToken) return;
      const failure = billingError(err, i18n.language);
      setError(failure.message);
      setErrorCode(failure.code);
      if (failure.code === "checkout_expired") setCounterAttempt(value => value + 1);
    } finally {
      if (useAuthStore.getState().token === ownerToken) {
        busy.current = false;
        setLoading(false);
      }
    }
  }

  async function handleManage() {
    if (busy.current) return;
    const ownerToken = token;
    busy.current = true;
    setLoading(true);
    try {
      const response = await api.post<{ url: string }>("/billing/customer-portal-session", undefined, { expectedSession: ownerToken } as SessionBoundRequestConfig);
      if (useAuthStore.getState().token === ownerToken) window.location.href = response.data.url;
    } catch {
      if (useAuthStore.getState().token === ownerToken) setError(t("billing.portal.error"));
    } finally {
      if (useAuthStore.getState().token === ownerToken) { busy.current = false; setLoading(false); }
    }
  }

  if (counterError || accountError) {
    return <div className="mx-auto max-w-4xl px-5 py-10">
      <AccountDataLink />
      <NotificationPreferencesLink />
      <LoadError onRetry={() => setCounterAttempt((attempt) => attempt + 1)} />
    </div>;
  }

  if (!counter || accountLoading) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 text-navy">
        <AccountDataLink />
        <NotificationPreferencesLink />
        {t("billing.loading")}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      <AccountDataLink />
      <NotificationPreferencesLink />
      {/* Why the athlete is here. Landing on a pricing table with no
          explanation reads as a paywall slammed shut; one line naming the
          reason is the difference between "start your trial" and "you have
          been cut off". */}
      {arrival === "welcome" ? (
        <div className="mb-6 rounded-lg border border-amber bg-amber/10 px-4 py-3 text-[14px] text-navy">
          <p className="font-medium">{t("billing.paywall.welcome_title")}</p>
          <p className="mt-1 text-taupe">{t("billing.paywall.welcome_body")}</p>
        </div>
      ) : arrival ? (
        <div className="mb-6 rounded-lg border border-coral bg-coral/10 px-4 py-3 text-[14px] text-navy">
          <p className="font-medium">{t("billing.paywall.locked_title")}</p>
          <p className="mt-1 text-taupe">
            {t(`billing.paywall.reason.${arrival}`, {
              defaultValue: t("billing.paywall.reason.no_subscription"),
            })}
          </p>
        </div>
      ) : null}

      <h1 className="font-serif text-3xl font-medium tracking-tight text-navy">
        {t("billing.pricing.title")}
      </h1>
      <p className="mt-2 text-[15px] text-taupe">
        {arrival === "welcome"
          ? t("billing.paywall.trial_subtitle")
          : t("billing.pricing.subtitle")}
      </p>

      <p className="mt-3 text-sm text-taupe">{billingCopy(i18n.language, 8)}</p>

      <div className="mt-6 flex items-center gap-3">
        <span className="text-sm text-taupe">
          {t("billing.pricing.currency_label")}:
        </span>
        {(["GBP", "EUR", "USD"] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCurrency(c)}
            disabled={!!pending && pending.currency !== c}
            className={`rounded-md px-3 py-1 text-sm ${
              currency === c
                ? "bg-amber text-navy-deep"
                : "bg-cream text-navy hover:bg-cream-light"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {(counter.founding_remaining > 0 || pending?.tier === "founding") && (
          <div className="rounded-lg border-2 border-amber bg-cream p-6">
            <div className="mb-2 text-xs font-semibold uppercase text-amber">
              {t("billing.pricing.founding_label")}
            </div>
            <h2 className="text-2xl font-bold text-navy">
              {t("billing.pricing.founding_title")}
            </h2>
            <div className="mb-4 mt-1 text-sm text-taupe">
              {t("billing.pricing.founding_remaining", {
                count: counter.founding_remaining,
              })}
            </div>
            <div className="mb-4 text-4xl font-bold text-navy">
              {PRICES.founding[currency]}
              <span className="text-base font-normal text-taupe">
                /{t("billing.pricing.month")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleSubscribe("founding")}
              disabled={loading || (!!pending && pending.tier !== "founding")}
              className="w-full rounded-md bg-amber py-3 font-bold text-navy-deep hover:bg-amber-dark hover:text-cream-light disabled:opacity-50"
            >
              {loading
                ? t("billing.pricing.loading")
                : t("billing.pricing.subscribe_founding")}
            </button>
          </div>
        )}

        <div className="rounded-lg border border-cream-border bg-cream-light p-6">
          <h2 className="text-2xl font-bold text-navy">
            {t("billing.pricing.standard_title")}
          </h2>
          <div className="mb-4 mt-1 text-sm text-taupe">
            {t("billing.pricing.standard_description")}
          </div>
          <div className="mb-4 text-4xl font-bold text-navy">
            {PRICES.standard[currency]}
            <span className="text-base font-normal text-taupe">
              /{t("billing.pricing.month")}
            </span>
          </div>
          <button
            type="button"
            onClick={() => handleSubscribe("standard")}
            disabled={loading || (!!pending && pending.tier !== "standard")}
            className="w-full rounded-md bg-navy py-3 text-white hover:bg-navy-deep disabled:opacity-50"
          >
            {loading
              ? t("billing.pricing.loading")
              : t("billing.pricing.subscribe_standard")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-coral p-4 text-coral">
          <p role="alert">{error}</p>
          {errorCode === "subscription_exists" && <button type="button" className="mt-3 underline" disabled={loading} onClick={() => void handleManage()}>{t("billing.portal.manage_button")}</button>}
          {["checkout_complete", "checkout_in_progress"].includes(errorCode) && <Link className="mt-3 block underline" to="/billing/success">{billingCopy(i18n.language, 6)}</Link>}
        </div>
      )}
    </div>
  );
}
